<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="flex items-center gap-3">
                <div class="cursor-pointer" data-test="toggle-favorite" @click="toggleFavorite()">
                    <font-awesome-icon
                        widthAuto
                        v-if="group.is_favorite"
                        :icon="faStar"
                        class="text-amber-400"
                    ></font-awesome-icon>
                    <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
                </div>
                <div>
                    {{ group.name }}
                </div>
            </div>
        </vx-table-row-title>

        <vx-table-row-item data-test="external-id">
            <template v-slot:label> External ID: </template>
            <span class="break-all">{{ group.group_id }}</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="team">
            <template v-slot:label> Team: </template>
            <div class="text-sm font-bold line-clamp-2">
                <vx-badge color="slate" size="xs">{{ group.team_id }}</vx-badge> {{ group.team_name }}
            </div>
        </vx-table-row-item>

        <vx-table-row-item data-test="team-status">
            <template v-slot:label> Team Status: </template>
            <vx-popover hover placement="top" arrow>
                <span class="capitalize">{{ group.team_status }}</span>
                <template v-if="group.team_status_reason" v-slot:content>
                    <div class="max-w-xs">
                        {{ group.team_status_reason }}
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item data-test="org-unit">
            <template v-slot:label> OU: </template>
            <vx-popover v-if="group.org_unit_name" hover :arrow="true" placement="top">
                <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                <template v-slot:content>
                    <span class="text-sm font-normal">{{ group.org_unit_name }}</span>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" size="sm" type="button" color="muted" outline class="w-9">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item :href="`/kiosk/teams/${group.team_id}/groups/${group.group_id}`">
                        View
                        <font-awesome-icon widthAuto :icon="faEye"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item v-if="group.is_favorite" @click="toggleFavorite()">
                        Remove Favorite
                        <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else @click="toggleFavorite()">
                        Favorite
                        <font-awesome-icon widthAuto :icon="faStarReg"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                :href="`/spark/kiosk/users/impersonate/${group.team_owner_id}?team_id=${group.team_id}&group_id=${group.group_id}`"
                :disabled="group.team_owner_id === currentUserId || group.team_status === 'closed'"
                size="sm"
                data-test="impersonate"
                outline
                color="muted"
            >
                Impersonate <font-awesome-icon widthAuto :icon="faMasksTheater" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faBuildingFlag, faEllipsis, faEye, faStar, faMasksTheater } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { adminGroupsClient } from '~/services';
import { ref } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';

const group = defineModel({
    required: true,
    type: Object,
});

const currentUserId = Spark.state.user.id;

const toasted = useToasted();

const togglingFavorite = ref(false);
const toggleFavorite = async () => {
    if (togglingFavorite.value) {
        return;
    }
    togglingFavorite.value = true;
    try {
        if (group.value.is_favorite) {
            await adminGroupsClient.deleteFavoriteGroup(group.value.team_id, group.value.group_id);
            group.value.is_favorite = false;
            toasted.success(
                `${group.value.group_id} - ${group.value.name} has been removed from your favorite groups.`
            );
        } else {
            await adminGroupsClient.favoriteGroup(group.value.team_id, group.value.group_id);
            group.value.is_favorite = true;
            toasted.success(`${group.value.group_id} - ${group.value.name} has been added to your favorite groups.`);
        }
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        togglingFavorite.value = false;
    }
};
</script>
